/**
 * Data Import Page.
 *
 * CSV import with preview, smart field mapping, validation feedback,
 * progress indicator, downloadable templates, and export.
 */

import { useState } from "react";
import {
  Upload,
  FileSpreadsheet,
  Check,
  AlertCircle,
  Download,
  Loader2,
} from "lucide-react";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Button from "@/components/ui/Button";
import ExportButton from "@/components/ui/ExportButton";
import Card from "@/components/ui/Card";
import { downloadBlob } from "@/utils/downloadBlob";

type EntityType = "assets" | "customers" | "vendors";

// Smart column name variations for auto-mapping.
const COLUMN_ALIASES: Record<string, string[]> = {
  name: [
    "name",
    "asset_name",
    "product_name",
    "item_name",
    "title",
    "description",
  ],
  category: ["category", "type", "asset_type", "product_type", "class"],
  purchase_price: ["purchase_price", "price", "cost", "unit_price", "amount"],
  year: ["year", "model_year", "yr"],
  make: ["make", "manufacturer", "brand", "mfg"],
  model: ["model", "model_name", "product_model"],
  serial_number: ["serial_number", "serial", "sn", "serial_no"],
  vin: ["vin", "vehicle_id", "vehicle_identification"],
  location: ["location", "warehouse", "yard", "storage_location"],
  lot_number: ["lot_number", "lot", "lot_no", "batch"],
  email: ["email", "email_address", "e_mail"],
  phone: ["phone", "phone_number", "telephone", "tel"],
  company_name: ["company_name", "company", "business", "org", "organization"],
  contact_name: [
    "contact_name",
    "contact",
    "contact_person",
    "primary_contact",
  ],
};

const ASSET_TEMPLATE_HEADERS = [
  "name",
  "category",
  "purchase_price",
  "year",
  "make",
  "model",
  "serial_number",
  "vin",
  "lot_number",
  "location",
  "description",
];

const CUSTOMER_TEMPLATE_HEADERS = [
  "name",
  "company_name",
  "email",
  "phone",
  "contact_name",
];

const VENDOR_TEMPLATE_HEADERS = [
  "name",
  "company_name",
  "email",
  "phone",
  "contact_name",
];

function downloadTemplate(entityType: EntityType) {
  const headers =
    entityType === "assets"
      ? ASSET_TEMPLATE_HEADERS
      : entityType === "customers"
        ? CUSTOMER_TEMPLATE_HEADERS
        : VENDOR_TEMPLATE_HEADERS;

  const sampleRows =
    entityType === "assets"
      ? [
          "2025 Ford F150,vehicle,45000,2025,Ford,F150,SN-12345,,LOT-001,Warehouse A,White crew cab",
          "John Deere 6120M,tractor,85000,2023,John Deere,6120M,SN-67890,,LOT-002,North Lot,Low hours",
        ]
      : entityType === "customers"
        ? [
            "John Smith,Smith Farms LLC,john@smithfarms.com,555-0101,John Smith",
            "Jane Doe,Doe Equipment,jane@doeequip.com,555-0102,Jane Doe",
          ]
        : [
            "ACME Parts,ACME Corp,sales@acme.com,555-0201,Bob Jones",
            "Quality Supply,Quality LLC,info@quality.com,555-0202,Alice Brown",
          ];

  const csv = [headers.join(","), ...sampleRows].join("\n");
  downloadBlob(
    new Blob([csv], { type: "text/csv" }),
    `${entityType}_import_template.csv`,
  );
}

function smartAutoMap(columns: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const col of columns) {
    const normalized = col.toLowerCase().trim().replace(/\s+/g, "_");
    for (const [field, aliases] of Object.entries(COLUMN_ALIASES)) {
      if (aliases.includes(normalized)) {
        result[field] = col;
        break;
      }
    }
  }
  return result;
}

function DataImportPage() {
  const [entityType, setEntityType] = useState<EntityType>("assets");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    columns: string[];
    sample_rows: Record<string, string>[];
    row_count: number;
  } | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  const handleFileSelect = async (f: File) => {
    setFile(f);
    setResult(null);
    setImportProgress(0);
    const formData = new FormData();
    formData.append("file", f);
    try {
      const resp = await apiClient.post(
        "/api/v1/data/import/preview",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(resp.data);
      setMapping(smartAutoMap(resp.data.columns));
    } catch {
      setPreview(null);
      toast.error("Failed to parse file", "Check that it is a valid CSV.");
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setIsImporting(true);
    setImportProgress(10);
    const formData = new FormData();
    formData.append("file", file);

    if (entityType === "assets") {
      formData.append("name_col", mapping["name"] || "name");
      formData.append("category_col", mapping["category"] || "category");
      formData.append(
        "price_col",
        mapping["purchase_price"] || mapping["price"] || "purchase_price",
      );
      if (mapping["year"]) formData.append("year_col", mapping["year"]);
      if (mapping["make"]) formData.append("make_col", mapping["make"]);
      if (mapping["model"]) formData.append("model_col", mapping["model"]);
      if (mapping["serial_number"])
        formData.append("serial_col", mapping["serial_number"]);
      if (mapping["vin"]) formData.append("vin_col", mapping["vin"]);
      if (mapping["location"])
        formData.append("location_col", mapping["location"]);
    } else {
      formData.append("name_col", mapping["name"] || "name");
      if (mapping["email"]) formData.append("email_col", mapping["email"]);
      if (mapping["phone"]) formData.append("phone_col", mapping["phone"]);
      if (mapping["company_name"] || mapping["company"])
        formData.append(
          "company_col",
          mapping["company_name"] || mapping["company"] || "",
        );
      if (mapping["contact_name"] || mapping["contact"])
        formData.append(
          "contact_col",
          mapping["contact_name"] || mapping["contact"] || "",
        );
    }

    setImportProgress(30);
    try {
      const resp = await apiClient.post(
        `/api/v1/data/import/${entityType}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportProgress(100);
      setResult(resp.data);
      if (resp.data.errors.length === 0) {
        toast.success(
          "Import complete",
          `${resp.data.imported} ${entityType} imported.`,
        );
      } else {
        toast.warning(
          "Import completed with errors",
          `${resp.data.imported} imported, ${resp.data.errors.length} errors.`,
        );
      }
    } catch {
      setImportProgress(0);
      setResult({
        imported: 0,
        skipped: 0,
        errors: ["Import failed. Check your file format and column mapping."],
      });
      toast.error("Import failed", "Check your file format.");
    } finally {
      setIsImporting(false);
    }
  };

  const mappedFieldCount = Object.values(mapping).filter(Boolean).length;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          Import &amp; Export Data
        </h2>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">
          Bulk import from CSV or export your data for external use.
        </p>
      </div>

      {/* Entity Type Selector + Template Download */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {(["assets", "customers", "vendors"] as EntityType[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setEntityType(t);
                setPreview(null);
                setFile(null);
                setResult(null);
              }}
              className={`rounded-[var(--radius-full)] px-4 py-1.5 text-sm font-medium capitalize transition-all ${
                entityType === t
                  ? "bg-brand-500/15 text-brand-600"
                  : "text-[var(--text-secondary)] hover:bg-[var(--color-surface-hover)]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => downloadTemplate(entityType)}
        >
          <Download className="h-3.5 w-3.5" />
          Download Template
        </Button>
      </div>

      {/* Upload Area */}
      {!preview && (
        <Card
          className="text-center cursor-pointer hover-glow"
          onClick={() => document.getElementById("csv-input")?.click()}
        >
          <Upload className="mx-auto h-10 w-10 text-[var(--text-tertiary)]" />
          <p className="mt-3 text-sm text-[var(--text-secondary)]">
            Click to select a CSV file or drag and drop
          </p>
          <p className="mt-1 text-xs text-[var(--text-tertiary)]">
            First row should be column headers.{" "}
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadTemplate(entityType);
              }}
              className="text-brand-600 hover:underline"
            >
              Download a template
            </button>{" "}
            to get started.
          </p>
          <input
            id="csv-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) =>
              e.target.files?.[0] && handleFileSelect(e.target.files[0])
            }
          />
        </Card>
      )}

      {/* Preview */}
      {preview && !result && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-brand-500" />
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {file?.name}
                </p>
                <p className="text-xs text-[var(--text-tertiary)]">
                  {preview.row_count} rows &middot; {preview.columns.length}{" "}
                  columns &middot;{" "}
                  <span className="text-brand-600">
                    {mappedFieldCount} fields mapped
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Column mapping status */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            {Object.entries(mapping).map(([field, col]) => (
              <span
                key={field}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--fill-success)] px-2.5 py-0.5 text-[10px] font-medium text-[var(--text-success)]"
              >
                <Check className="h-2.5 w-2.5" />
                {field} → {col}
              </span>
            ))}
          </div>

          {/* Sample data preview */}
          <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)]">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[var(--color-surface-tertiary)]">
                  <th className="px-3 py-2 text-left font-medium text-[var(--text-tertiary)] w-8">
                    #
                  </th>
                  {preview.columns.map((col) => (
                    <th
                      key={col}
                      className="px-3 py-2 text-left font-medium text-[var(--text-secondary)]"
                    >
                      {col}
                      {Object.values(mapping).includes(col) && (
                        <Check className="inline h-3 w-3 text-[var(--text-success)] ml-1" />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.slice(0, 3).map((row, i) => (
                  <tr
                    key={i}
                    className="border-t border-[var(--color-border-light)]"
                  >
                    <td className="px-3 py-1.5 text-[var(--text-tertiary)]">
                      {i + 1}
                    </td>
                    {preview.columns.map((col) => (
                      <td
                        key={col}
                        className="px-3 py-1.5 text-[var(--text-primary)]"
                      >
                        {row[col] || (
                          <span className="text-[var(--text-tertiary)]">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Progress bar */}
          {isImporting && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[var(--text-secondary)]">
                  <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                  Importing...
                </span>
                <span className="text-xs text-[var(--text-tertiary)]">
                  {importProgress}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-[var(--surface-sunken)]">
                <div
                  className="h-full rounded-full bg-brand-500 transition-all duration-500"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
            >
              Choose Different File
            </Button>
            <Button
              size="sm"
              onClick={handleImport}
              isLoading={isImporting}
              disabled={isImporting}
            >
              Import {preview.row_count} Rows
            </Button>
          </div>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card>
          <div className="flex items-center gap-3">
            {result.errors.length === 0 ? (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fill-success)]">
                <Check className="h-6 w-6 text-[var(--text-success)]" />
              </div>
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fill-warning)]">
                <AlertCircle className="h-6 w-6 text-[var(--text-warning)]" />
              </div>
            )}
            <div>
              <p className="text-lg font-bold text-[var(--text-primary)]">
                {result.imported} {entityType} imported
              </p>
              {result.skipped > 0 && (
                <p className="text-sm text-[var(--text-secondary)]">
                  {result.skipped} rows skipped
                </p>
              )}
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="mt-4 rounded-[var(--radius-lg)] bg-[var(--fill-danger)] dark:bg-red-900/10 p-4 max-h-48 overflow-y-auto">
              <p className="text-xs font-semibold text-[var(--text-danger)] mb-2">
                {result.errors.length} error
                {result.errors.length !== 1 ? "s" : ""}:
              </p>
              <div className="space-y-1">
                {result.errors.map((e, i) => (
                  <p
                    key={i}
                    className="text-xs text-[var(--text-danger)] flex gap-2"
                  >
                    <span className="text-[var(--text-danger)]/60 shrink-0">
                      Row {i + 1}:
                    </span>
                    {e}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setResult(null);
                setPreview(null);
                setFile(null);
                setImportProgress(0);
              }}
            >
              Import More
            </Button>
          </div>
        </Card>
      )}

      {/* Export Section */}
      <Card padding="sm" className="card-gradient">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[var(--text-primary)]">
              Export Data
            </p>
            <p className="text-xs text-[var(--text-tertiary)]">
              Every row, as CSV or Excel. The CSV keeps the{" "}
              <code className="rounded bg-[var(--surface-sunken)] px-1 py-0.5 text-[11px]">
                traderoute_id
              </code>{" "}
              column, which is what lets you correct a copy and import it back
              over the same records.
            </p>
          </div>
          <div className="flex gap-2">
            <ExportButton
              url="/api/v1/data/export/assets"
              fallbackFilename="assets"
              label="Assets"
            />
            <ExportButton
              url="/api/v1/data/export/customers"
              fallbackFilename="customers"
              label="Customers"
            />
            <ExportButton
              url="/api/v1/data/export/vendors"
              fallbackFilename="vendors"
              label="Vendors"
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

export default DataImportPage;
